import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, IconButton, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN   = '#1b4332';
const RED     = '#dc2626';
const BLUE    = '#1565c0';

// Section colours — matching the sanding/ddsaw style
const COL_PF      = '#1b4332';   // green  — PF
const COL_MUF     = '#1e3a5f';   // deep blue — MUF
const COL_PACKING = '#92400e';   // brown — Packing-PLY
const TBL_HDR_BG  = '#2d3748';   // dark header (matches all other reports)

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.72rem', textTransform: 'uppercase',
  backgroundColor: TBL_HDR_BG,
  borderRight: '1px solid rgba(255,255,255,0.15)', py: 1.0, px: 0.8,
  whiteSpace: 'nowrap', textAlign: 'center',
};
const CELL_SX = { py: 0.8, px: 0.8, fontSize: '0.76rem', textAlign: 'center' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}
function fmtElapsed(hours, minutes) {
  if (hours === null || hours === undefined) return '—';
  return `${hours}h ${String(minutes || 0).padStart(2, '0')}m`;
}
function fmtDate(val) {
  if (!val) return '—';
  const s = String(val).substring(0, 10);
  const [yr, mo, dy] = s.split('-');
  return `${dy}/${mo}/${yr}`;
}

// ─── Section title bar (matches sanding/ddsaw style) ─────────────────────────
function SectionTitle({ title, color, totalCount, overdueCount, thresholdHours }) {
  return (
    <Box sx={{
      background: color, px: 2, py: 1.2, borderRadius: '4px 4px 0 0',
      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'
    }}>
      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '0.3px' }}>
        {title}
      </Typography>
      {thresholdHours && (
        <Box sx={{ background: 'rgba(255,255,255,0.15)', px: 1, py: 0.2, borderRadius: 1 }}>
          <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 600 }}>
            {thresholdHours}h threshold
          </Typography>
        </Box>
      )}
      <Box sx={{ background: 'rgba(255,255,255,0.15)', px: 1, py: 0.2, borderRadius: 1 }}>
        <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 600 }}>
          {totalCount} delayed
        </Typography>
      </Box>
      {overdueCount > 0 && (
        <Box sx={{ background: RED, px: 1, py: 0.2, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>
            ⚠ {overdueCount} OVERDUE
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Block table ──────────────────────────────────────────────────────────────
function BlockTable({ rows, title, thresholdHours, color, blockKey }) {
  const totalCount  = rows?.length || 0;

  return (
    <Box sx={{ mb: 3, borderRadius: 1, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <SectionTitle
        title={title} color={color}
        totalCount={totalCount} overdueCount={0}
        thresholdHours={thresholdHours}
      />

      {totalCount === 0 ? (
        <Box sx={{ background: '#f8faf9', border: `1px solid ${color}30`, borderTop: 'none', p: 2, borderRadius: '0 0 4px 4px' }}>
          <Typography sx={{ color: '#666', fontSize: '0.82rem', textAlign: 'center' }}>
            No pending lots in this category
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 420, overflowX: 'auto', border: `1px solid ${color}25`, borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1150 }}>
            <TableHead>
              <TableRow>
                {['SR', 'LOT NO', 'CP NAME', 'HP NAME', 'TABLE', 'ASSEMBLER', 'SIZE', 'THICK', 'GLUE', 'QUALITY', 'HP SHIFT', 'ASSY DATE', 'HP DATE', 'START', 'END', 'PCS', 'CBM', 'WAIT TIME', 'STATUS'].map(h => (
                  <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => {
                return (
                  <TableRow key={i} sx={{
                    backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9',
                    '&:hover': { backgroundColor: '#e8f5e9' },
                  }}>
                    <TableCell sx={CELL_SX}>{i + 1}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: BLUE }}>{row.LOT_NUMBER || '—'}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{row.CPNAME || '—'}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#7f1d1d' }}>{row.HPNAME || '—'}</TableCell>
                    <TableCell sx={CELL_SX}>{row.ASSEMBLY_TABLENO || '—'}</TableCell>
                    <TableCell sx={{ ...CELL_SX, textAlign: 'left', px: 1 }}>{row.ASSEMBLER_NAME || '—'}</TableCell>
                    <TableCell sx={CELL_SX}>{row.SIZE || '—'}</TableCell>
                    <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: blockKey === 'MUF' ? COL_MUF : GREEN }}>
                      {row.GLUETYPE || '—'}
                    </TableCell>
                    <TableCell sx={CELL_SX}>{row.QUALITY || '—'}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: (row.HP_SHIFT || row.SHIFT) === 'NIGHT' ? BLUE : '#2e7d32' }}>
                      {row.HP_SHIFT || row.SHIFT || '—'}
                    </TableCell>
                    <TableCell sx={CELL_SX}>{fmtDate(row.ASSEMBLY_DATE)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: BLUE }}>{fmtDate(row.HP_DATE)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.ASSEMBLYSTART || '—'}</TableCell>
                    <TableCell sx={CELL_SX}>{row.ASSEMBLYEND || '—'}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: RED }}>
                      {fmtElapsed(row.HOURS_ELAPSED, row.MINUTES_ELAPSED)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.5 }}>
                      {blockKey === 'PACKING'
                        ? <Box sx={{ background: '#dbeafe', color: BLUE, fontSize: '0.65rem', fontWeight: 600, px: 0.8, py: 0.2, borderRadius: 0.5, display: 'inline-block' }}>RECORDED</Box>
                        : <Box sx={{ background: RED, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 0.5, display: 'inline-block' }}>DELAYED</Box>
                      }
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── Summary strip (horizontal, like hotpress reports) ────────────────────────
function SummaryStrip({ summary, pfHours, mufHours }) {
  if (!summary) return null;
  const s = summary;

  return (
    <Card sx={{
      mb: 3, borderRadius: 2,
      background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box display="flex" flexWrap="wrap" gap={3} alignItems="center" justifyContent="space-between">
          {/* PF */}
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
              PF delayed (&gt;{pfHours}h)
            </Typography>
            <Box display="flex" alignItems="baseline" gap={1}>
              <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                {s.PF_TOTAL || 0}
              </Typography>
              <Typography sx={{ color: '#a7f3d0', fontSize: '0.82rem' }}>lots</Typography>
            </Box>
          </Box>

          <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />

          {/* MUF */}
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
              MUF delayed (&gt;{mufHours}h)
            </Typography>
            <Box display="flex" alignItems="baseline" gap={1}>
              <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                {s.MUF_TOTAL || 0}
              </Typography>
              <Typography sx={{ color: '#a7f3d0', fontSize: '0.82rem' }}>lots</Typography>
            </Box>
          </Box>

          <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />

          {/* Packing */}
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
              Packing-PLY
            </Typography>
            <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
              {s.PACKING_TOTAL || 0}
            </Typography>
          </Box>

          <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />

          {/* Totals */}
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
              Total PCS / CBM
            </Typography>
            <Typography sx={{ color: '#fff', fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>
              {fmtNum(s.TOTAL_PCS, 0)}
              <Typography component="span" sx={{ fontSize: '0.9rem', color: '#a7f3d0', ml: 1 }}>
                / {fmtNum(s.TOTAL_CBM, 3)} CBM
              </Typography>
            </Typography>
          </Box>

        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────
const HotpressDelayView = ({ onBackClick, user }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate]     = useState(null);
  const [pfHours, setPfHours]   = useState(4);
  const [mufHours, setMufHours] = useState(2);
  const [shift, setShift]       = useState('BOTH');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);

  const fetchData = async () => {
    if (!fromDate || !toDate) { setError('Please select both dates'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/hotpressdelay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date:        fromDate.format('YYYY-MM-DD'),
          to_date:          toDate.format('YYYY-MM-DD'),
          delay_hours_pf:   pfHours,
          delay_hours_muf:  mufHours,
          shift:            shift,
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data) throw new Error('No data returned');
      setData(apiData.data);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    [
      { idx: 0, label: 'PF Pending' },
      { idx: 1, label: 'MUF Pending' },
      { idx: 2, label: 'Packing-PLY Pending' },
      { idx: 3, label: 'Summary' },
    ].forEach(({ idx, label }) => {
      if (data[idx]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[idx]), label);
    });
    XLSX.writeFile(wb, `HotpressDelay_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.xlsx`);
  };

  const summary = data?.[3]?.[0] || null;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* Search card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}><ArrowBackIcon /></IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <AccessTimeIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Hotpress Delay Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Assembly-to-hotpress delay · PF ({pfHours}h threshold) · MUF ({mufHours}h threshold) · Packing-PLY · {shift} SHIFT
              </Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center" flexWrap="wrap">
              <DatePicker label="From Date" value={fromDate} onChange={setFromDate}
                slotProps={{ textField: { size: 'small', sx: { flex: 1, minWidth: 155 } } }} />
              <DatePicker label="To Date" value={toDate} onChange={setToDate} minDate={fromDate}
                slotProps={{ textField: { size: 'small', sx: { flex: 1, minWidth: 155 } } }} />
              <TextField
                label="PF Delay (hrs)" type="number" size="small"
                value={pfHours} onChange={e => setPfHours(Number(e.target.value))}
                inputProps={{ min: 1, max: 24 }}
                sx={{ width: 130 }}
              />
              <TextField
                label="MUF Delay (hrs)" type="number" size="small"
                value={mufHours} onChange={e => setMufHours(Number(e.target.value))}
                inputProps={{ min: 1, max: 24 }}
                sx={{ width: 140 }}
              />
              <ToggleButtonGroup
                value={shift} exclusive
                onChange={(_, v) => { if (v) setShift(v); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2 },
                  '& .Mui-selected': { backgroundColor: `${GREEN} !important`, color: '#fff !important' },
                }}
              >
                <ToggleButton value="DAY">DAY</ToggleButton>
                <ToggleButton value="NIGHT">NIGHT</ToggleButton>
                <ToggleButton value="BOTH">BOTH</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{ minWidth: 130, backgroundColor: GREEN, '&:hover': { backgroundColor: '#0f2419' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <SummaryStrip summary={summary} pfHours={pfHours} mufHours={mufHours} />

          <Box display="flex" justifyContent="flex-end" mb={2} gap={1}>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', border: `1px solid ${GREEN}40`, borderRadius: 1, px: 2 }}>
              Download Excel
            </Button>
            <Button size="small" variant="outlined"
              onClick={() => {
                const url = `${API_URL}/api/hotpress-delay/preview?from_date=${fromDate.format('YYYY-MM-DD')}&to_date=${toDate.format('YYYY-MM-DD')}&pf=${pfHours}&muf=${mufHours}&shift=${shift}`;
                window.open(url, '_blank');
              }}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', border: `1px solid ${GREEN}40`, borderRadius: 1, px: 2 }}>
              Preview Image
            </Button>
          </Box>

          <BlockTable rows={data[0]} title="PF — Glue Type"      thresholdHours={pfHours}  color={COL_PF}      blockKey="PF" />
          <BlockTable rows={data[1]} title="MUF — Glue Type"     thresholdHours={mufHours} color={COL_MUF}     blockKey="MUF" />
          <BlockTable rows={data[2]} title="PACKING-PLY — Quality" thresholdHours={null}   color={COL_PACKING} blockKey="PACKING" />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
            <WhatsAppSendButton
              user={user}
              label="Send Hotpress Delay Report to WhatsApp"
              apiUrl={fromDate && toDate ? `${API_URL}/api/hotpress-delay/send` : null}
              payload={{ from_date: fromDate?.format('YYYY-MM-DD'), to_date: toDate?.format('YYYY-MM-DD'), delay_hours_pf: pfHours, delay_hours_muf: mufHours, shift }}
              disabled={!data}
            />
          </Box>
        </>
      )}

      {!data && !loading && !error && (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 3, background: '#fafafa', boxShadow: 'none', border: '2px dashed #e0e0e0' }}>
          <AccessTimeIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>No data to display</Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>Select a date range and click Search</Typography>
        </Card>
      )}
    </Container>
  );
};

export default HotpressDelayView;
